import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { CurrentUser, DataSource, Page, Role } from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";

/**
 * How the signed-in user is read, written down once so the hook below and
 * `scripts/check-permissions-refresh.ts` read it the same way.
 *
 * `refetchOnWindowFocus: "always"` rather than the client's default, which
 * only refetches an answer older than the stale time (thirty seconds). The
 * permissions in this answer decide what every screen offers, and the common
 * way they change is someone editing a role in another tab and switching back
 * to this one: inside thirty seconds of the last read that showed nothing new,
 * and the person was left wondering whether the grant had worked (#523). It is
 * one small request each time a tab regains focus, not a poll.
 */
export function currentUserQueryOptions(data: DataSource) {
  return {
    queryKey: ["currentUser"],
    queryFn: () => data.currentUser(),
    refetchOnWindowFocus: "always",
  } as const;
}

/**
 * The signed-in fixture identity.
 *
 * Same query key `RoleBuilder.tsx` and every other screen would use for
 * their own `currentUser()` call, so the account menu does not pay for a
 * second fetch of something already in cache.
 */
export function useCurrentUser(): UseQueryResult<CurrentUser> {
  const data = useDataSource();
  return useQuery(currentUserQueryOptions(data));
}

/**
 * The seeded roles — Administrator, Operations, Warehouse, Accounts.
 *
 * Same query key as `RoleBuilder.tsx`'s own `listRoles()` call, so the
 * persona switcher and the account menu share its cache entry instead of
 * each fetching their own copy.
 */
export function useRoles(): UseQueryResult<Page<Role>> {
  const data = useDataSource();
  return useQuery({
    queryKey: ["settings", "roles"],
    queryFn: () => data.settings.listRoles(),
  });
}
